import api, { getHHMailTemplates } from '../services/api';

export type AutoParseAction = 'invite' | 'clarify' | 'reject';

export interface AutoParseBucket {
  id: string;
  label: string;
  defaultAction: AutoParseAction;
  match: (rating: number) => boolean;
}

export const AUTO_PARSE_ACTION_LABELS: Record<AutoParseAction, string> = {
  invite: 'Пригласить',
  clarify: 'Уточнить опыт',
  reject: 'Отказать',
};

export const AUTO_PARSE_BUCKETS: AutoParseBucket[] = [
  { id: 'gte85', label: '>=85%', defaultAction: 'invite', match: (rating) => rating >= 85 },
  { id: '70_84', label: '70-84%', defaultAction: 'clarify', match: (rating) => rating >= 70 && rating < 85 },
  { id: '50_69', label: '50-69%', defaultAction: 'clarify', match: (rating) => rating >= 50 && rating < 70 },
  { id: '30_49', label: '49-30%', defaultAction: 'reject', match: (rating) => rating >= 30 && rating < 50 },
  { id: 'lt30', label: '<30%', defaultAction: 'reject', match: (rating) => rating < 30 },
];

export const createDefaultAutoParseConfig = (): Record<string, AutoParseAction> =>
  AUTO_PARSE_BUCKETS.reduce((acc, bucket) => {
    acc[bucket.id] = bucket.defaultAction;
    return acc;
  }, {} as Record<string, AutoParseAction>);

export const getResponseAutoParseRating = (
  response: any,
  aiEvaluations: Record<string, any>,
  fallbackVacancyId?: string | number | null,
): number | null => {
  const responseVacancyId = response?.sourceVacancyId || fallbackVacancyId;
  const evaluation = responseVacancyId
    ? aiEvaluations[`${responseVacancyId}_${response?.id}`]
    : undefined;
  const rawRating = evaluation?.rating ?? response?.aiRating ?? response?.rating ?? null;
  const rating = Number(rawRating);
  return Number.isFinite(rating) ? rating : null;
};

export const getAutoParseBucket = (rating: number) =>
  AUTO_PARSE_BUCKETS.find((bucket) => bucket.match(rating)) ?? null;

export const getAutoParseBucketCounts = (
  responses: any[],
  aiEvaluations: Record<string, any>,
  fallbackVacancyId?: string | number | null,
) => {
  const counts = AUTO_PARSE_BUCKETS.reduce((acc, bucket) => {
    acc[bucket.id] = 0;
    return acc;
  }, {} as Record<string, number>);

  for (const response of responses) {
    const rating = getResponseAutoParseRating(response, aiEvaluations, fallbackVacancyId);
    if (rating === null) continue;
    const bucket = getAutoParseBucket(rating);
    if (bucket) counts[bucket.id] += 1;
  }

  return counts;
};

export const groupResponsesForAutoParse = (
  responses: any[],
  aiEvaluations: Record<string, any>,
  fallbackVacancyId: string | number | null | undefined,
  config: Record<string, AutoParseAction>,
) => {
  const grouped: Record<AutoParseAction, any[]> = {
    invite: [],
    clarify: [],
    reject: [],
  };
  let skippedWithoutRating = 0;

  for (const response of responses) {
    const rating = getResponseAutoParseRating(response, aiEvaluations, fallbackVacancyId);
    if (rating === null) {
      skippedWithoutRating++;
      continue;
    }
    const bucket = getAutoParseBucket(rating);
    if (!bucket) continue;
    const action = config[bucket.id] || bucket.defaultAction;
    grouped[action].push(response);
  }

  return { grouped, skippedWithoutRating };
};

export const findResponseHHAction = (response: any, actionType: 'invite' | 'reject') => {
  const actions = Array.isArray(response?.actions) ? response.actions : [];
  if (actionType === 'reject') {
    return actions.find((action: any) => action.id === 'discard_by_employer') ?? null;
  }

  const stateId = response?.employer_state?.id || response?.state?.id;
  if (stateId === 'phone_interview') {
    return actions.find((action: any) => action.id === 'interview')
      ?? actions.find((action: any) => action.id === 'phone_interview')
      ?? null;
  }

  return actions.find((action: any) => action.id === 'phone_interview')
    ?? actions.find((action: any) => action.id === 'interview')
    ?? null;
};

export const findActionTemplate = (action: any, preferredTemplateId: string) => {
  const templates = Array.isArray(action?.templates) ? action.templates : [];
  return templates.find((template: any) => template.id === preferredTemplateId) ?? templates[0] ?? null;
};

const normalizeTemplateValue = (value: any) =>
  String(value ?? '')
    .replace(/\u00A0|\u202F/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

export const loadHHActionTemplateText = async (template: any): Promise<string> => {
  if (!template?.id && !template?.name && !template?.url) return '';

  const selectedId = String(template?.id ?? '').trim();
  const selectedName = normalizeTemplateValue(template?.name);
  const isRejectAfterResponse = selectedId === 'discard_after_response';
  const isNotFit = isRejectAfterResponse || selectedName === normalizeTemplateValue('Не подходит');
  const isNotFitAfterInterview = selectedName === normalizeTemplateValue('Не подходит по итогам интервью');
  const isInviteAfterResponse =
    selectedId === 'invite_after_response'
    || selectedName.includes('первичный контакт')
    || (selectedName.includes('отклик') && selectedName.includes('приглас'));
  const preferredIds: string[] =
    isNotFit
      ? ['response_discard_mail', 'interview_discard_mail']
      : isNotFitAfterInterview
      ? ['interview_discard_mail']
      : isInviteAfterResponse
      ? ['response_invite_mail', 'invite_mail']
      : [];

  const data: any = await getHHMailTemplates();
  const raw = data?.templates ?? data;
  const list = Array.isArray(raw) ? raw : [];
  const bySelectedId = selectedId
    ? (list.find((item: any) => String(item?.id ?? '') === selectedId) ?? null)
    : null;
  const byPreferredId =
    preferredIds.length > 0
      ? (preferredIds
        .map((id) => list.find((item: any) => String(item?.id ?? '') === id))
        .find(Boolean) ?? null)
      : null;
  const byExactName = selectedName
    ? (list.find((item: any) => normalizeTemplateValue(item?.name) === selectedName) ?? null)
    : null;
  const found =
    bySelectedId
    ?? byPreferredId
    ?? byExactName
    ?? (isNotFit
      ? (list.find((item: any) => normalizeTemplateValue(item?.name).includes('не подходит') && normalizeTemplateValue(item?.name).includes('отклик'))
        ?? list.find((item: any) => normalizeTemplateValue(item?.name).includes('не подходит')))
      : null)
    ?? (isInviteAfterResponse
      ? (list.find((item: any) => normalizeTemplateValue(item?.name).includes('первичный контакт') && normalizeTemplateValue(item?.name).includes('отклик'))
        ?? list.find((item: any) => normalizeTemplateValue(item?.name).includes('приглас')))
      : null)
    ?? null;

  if (found?.text) return String(found.text);
  return '';
};

export const loadAutoParseActionTemplates = async (responses: any[]) => {
  const inviteAction = responses.map((response: any) => findResponseHHAction(response, 'invite')).find(Boolean);
  const rejectAction = responses.map((response: any) => findResponseHHAction(response, 'reject')).find(Boolean);
  const inviteTemplate = findActionTemplate(inviteAction, 'invite_after_response');
  const rejectTemplate = findActionTemplate(rejectAction, 'discard_after_response');
  const inviteTemplateForText = inviteTemplate ?? { id: 'invite_after_response' };
  const rejectTemplateForText = rejectTemplate ?? { id: 'discard_after_response' };

  const [inviteMessage, rejectMessage] = await Promise.all([
    loadHHActionTemplateText(inviteTemplateForText).catch(() => ''),
    loadHHActionTemplateText(rejectTemplateForText).catch(() => ''),
  ]);

  return {
    inviteTemplateId: inviteTemplate?.id ? String(inviteTemplate.id) : null,
    rejectTemplateId: rejectTemplate?.id ? String(rejectTemplate.id) : null,
    inviteMessage,
    rejectMessage,
  };
};

export const sendAutoParseHHAction = async ({
  responses,
  actionType,
  message,
  templateId,
  fallbackVacancyId,
  positionId,
  shouldStop,
}: {
  responses: any[];
  actionType: 'invite' | 'reject';
  message: string;
  templateId: string | null;
  fallbackVacancyId?: string | number | null;
  positionId?: string | number | null;
  shouldStop?: () => boolean;
}) => {
  let successCount = 0;
  let errorCount = 0;
  const successfulResponseIds: string[] = [];

  for (const response of responses) {
    if (shouldStop?.()) break;

    const selectedAction = findResponseHHAction(response, actionType);
    if (!selectedAction) {
      errorCount++;
      continue;
    }

    try {
      const responseId = response.id;
      const responseVacancyId = response.sourceVacancyId || fallbackVacancyId;
      const resumeId = response.resume?.id;
      const candidateName = [response.resume?.first_name, response.resume?.last_name].filter(Boolean).join(' ') || response.resume?.title;

      if (actionType === 'invite' && !resumeId) {
        errorCount++;
        continue;
      }

      await api.post(`/vacancies/${responseVacancyId}/responses/${responseId}/action`, {
        actionId: selectedAction.id,
        actionUrl: selectedAction.url,
        actionMethod: selectedAction.method,
        message: message.trim() || undefined,
        templateId: templateId || undefined,
        sendSms: true,
        resumeId: resumeId || undefined,
        candidateName: candidateName || undefined,
        firstName: response.resume?.first_name || undefined,
        lastName: response.resume?.last_name || undefined,
        middleName: response.resume?.middle_name || undefined,
        positionId: positionId || undefined,
      });

      successCount++;
      successfulResponseIds.push(String(responseId));
    } catch (error) {
      console.error(`Error performing auto-parse action for response ${response.id}:`, error);
      errorCount++;
    }
  }

  return { successCount, errorCount, successfulResponseIds };
};
